// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd
import { Button, Dropdown, Popover, type MenuProps } from "antd";
import {
  AppstoreOutlined,
  AudioMutedOutlined,
  AudioOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExpandOutlined,
  HighlightOutlined,
  FundProjectionScreenOutlined,
  LeftOutlined,
  MessageOutlined,
  PhoneOutlined,
  RightOutlined,
  PushpinFilled,
  SmileOutlined,
  TeamOutlined,
  SwapOutlined,
  VideoCameraAddOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { usePipDrag } from "./usePipDrag";
import { useSpeaking } from "../speaking";
import { EmojiPicker } from "./EmojiPicker";
import { qualityMenuGroup, backgroundMenuGroup } from "./qualityMenu";
import { AnnotationOverlay, type AnnotationConfig } from "./AnnotationOverlay";
import { screenBoardId } from "../whiteboard/protocol";
import type { WireOp } from "../whiteboard/types";
import type { CallQuality, VideoQuality } from "../rtc";
import type { BgMode } from "../bgeffect";
import { cameraLabel, micLabel } from "../devices";

// One tile in the grid: a participant's (or our own) video with a name label. The
// <video> stays mounted (audio) and is shown only when a video track is present;
// a track-change listener re-renders + re-attaches so a mid-call camera-on shows.
function Tile({
  stream,
  name,
  self,
  presenting,
  muted,
  speaking,
  handRaised,
  quality,
  pinned,
  onClick,
  annotation,
}: {
  readonly stream: MediaStream | null;
  readonly name: string;
  readonly self?: boolean;
  readonly presenting?: boolean;
  readonly muted?: boolean;
  readonly speaking?: boolean;
  readonly handRaised?: boolean;
  readonly quality?: CallQuality;
  readonly pinned?: boolean; // shown on the stage by an explicit pin
  readonly onClick?: () => void; // click to pin/unpin to the stage
  // When this tile is presenting, an annotation overlay anchored to its video (WB-010).
  readonly annotation?: AnnotationConfig | null;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const attach = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      if (el && stream) {
        el.srcObject = stream;
        void el.play().catch(() => undefined);
      }
    },
    [stream],
  );
  useEffect(() => {
    if (!stream) return;
    const onChange = () => {
      bump();
      const el = videoRef.current;
      if (el) {
        // Force-clear first: a track swap within the SAME stream (screen-share → camera)
        // otherwise leaves the element frozen on the last screen frame.
        const hasVideo = stream.getVideoTracks().some((t) => t.enabled);
        el.srcObject = null;
        el.srcObject = hasVideo ? stream : null;
        if (hasVideo) void el.play().catch(() => undefined);
      }
    };
    stream.addEventListener("addtrack", onChange);
    stream.addEventListener("removetrack", onChange);
    return () => {
      stream.removeEventListener("addtrack", onChange);
      stream.removeEventListener("removetrack", onChange);
    };
  }, [stream]);
  const hasVideo = !!stream?.getVideoTracks().some((t) => t.enabled);
  return (
    <div
      className={`group-tile${presenting ? " group-presenting" : ""}${speaking ? " group-speaking" : ""}${onClick ? " group-tile-click" : ""}`}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation(); // don't also toggle the auto-hiding chrome
              onClick();
            }
          : undefined
      }
      title={onClick ? (pinned ? "Unpin from stage" : "Pin to stage") : undefined}
    >
      <video
        ref={attach}
        autoPlay
        playsInline
        muted={self}
        className={`group-video${self ? " group-self" : ""}${presenting ? " group-screen" : ""}`}
        style={{ display: hasVideo ? "block" : "none" }}
      >
        <track kind="captions" />
      </video>
      {!hasVideo && <div className="group-monogram">{name.replace(/^@/, "").charAt(0).toUpperCase() || "?"}</div>}
      {presenting && annotation && (
        <AnnotationOverlay
          videoRef={videoRef}
          author={annotation.author}
          boardId={annotation.boardId}
          color={annotation.color}
          enabled={annotation.enabled}
          sendOp={annotation.sendOp}
          register={annotation.register}
        />
      )}
      {pinned && <PushpinFilled className="group-pin" title="Pinned to stage" />}
      {handRaised && <span className="group-hand" title="Hand raised">✋</span>}
      {muted && <AudioMutedOutlined className="group-mic-off" title="Muted" />}
      {!self && quality && (
        <span
          className={quality.state === "reconnecting" ? "group-quality group-quality-reconnecting" : `group-quality call-quality-${quality.state}`}
          title={quality.state === "reconnecting" ? "Reconnecting…" : `Connection: ${quality.state}`}
        >
          {quality.state === "reconnecting" ? "⟳" : <span className="call-quality-dot" />}
        </span>
      )}
      <span className="group-name">
        {presenting ? "🖥 " : ""}
        {name}
        {self ? " (you)" : ""}
        {presenting ? " · sharing screen" : ""}
      </span>
    </div>
  );
}

// reactionLeft spreads floating reactions horizontally (deterministic per id, 15%–85%).
function reactionLeft(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return 15 + (Math.abs(h) % 70);
}

// gridLayer maps the grid participant count to a simulcast layer: small calls show big tiles
// (high "f"); as the grid grows the tiles shrink, so step down to "h" then "q".
function gridLayer(count: number): string {
  if (count <= 2) return "f";
  if (count <= 4) return "h";
  return "q";
}

// Roster is the participants rail (the concept's right side): every participant with
// avatar, name, and live mic / raised-hand / speaking / screen-share / pinned state.
// Lists EVERYONE — even those not currently shown as a tile (large calls) — and a row
// click pins that person to the stage. Side column on desktop, overlay drawer on mobile.
function Roster({
  ids,
  count,
  myUserID,
  myName,
  nameOf,
  muted,
  mutedUsers,
  sharingScreen,
  presenting,
  speaking,
  raisedHands,
  pinned,
  onPin,
  onClose,
}: {
  readonly ids: string[];
  readonly count: number;
  readonly myUserID: string;
  readonly myName: string;
  readonly nameOf: (uid: string) => string;
  readonly muted: boolean;
  readonly mutedUsers: string[];
  readonly sharingScreen: boolean;
  readonly presenting: string[];
  readonly speaking: string[];
  readonly raisedHands: string[];
  readonly pinned: string | null;
  readonly onPin: (id: string) => void;
  readonly onClose: () => void;
}) {
  return (
    <aside className="group-roster" onClick={(e) => e.stopPropagation()}>
      <div className="group-roster-head">
        <span>
          {count} {count === 1 ? "participant" : "participants"}
        </span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} title="Hide participants" />
      </div>
      <div className="group-roster-list">
        {ids.map((id) => {
          const self = id === "__self";
          const dn = self ? myName : nameOf(id);
          const isMuted = self ? muted : mutedUsers.includes(id);
          const isPresenting = self ? sharingScreen : presenting.includes(id);
          const isSpeaking = speaking.includes(id);
          const isHand = self ? raisedHands.includes(myUserID) : raisedHands.includes(id);
          return (
            <button key={id} type="button" className={`group-roster-row${isSpeaking ? " is-speaking" : ""}`} onClick={() => onPin(id)} title={pinned === id ? "Unpin from stage" : "Pin to stage"}>
              <span className="group-roster-avatar">{dn.replace(/^@/, "").charAt(0).toUpperCase() || "?"}</span>
              <span className="group-roster-name">
                {dn}
                {self ? " (you)" : ""}
                {isPresenting ? " · sharing" : ""}
              </span>
              <span className="group-roster-icons">
                {isHand && <span title="Hand raised">✋</span>}
                {pinned === id && <PushpinFilled title="Pinned" />}
                {isMuted ? <AudioMutedOutlined className="muted" title="Muted" /> : <AudioOutlined title="Unmuted" />}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

// GroupCallView is the mesh group-call surface: a grid of participants (you + each
// peer) plus the call controls. Mirrors CallView's controls; the media elements stay
// mounted so audio survives minimise.
export function GroupCallView({
  active,
  minimized,
  oneToOne,
  channelName,
  localStream,
  participants,
  peerQuality,
  names,
  muted,
  cameraOff,
  sendingVideo,
  mics,
  cameras,
  currentMic,
  currentCamera,
  myName,
  onToggleMute,
  onToggleCamera,
  onShareScreen,
  sharingScreen,
  presenting,
  mutedUsers,
  onLeave,
  onMinimize,
  onRestore,
  onSelectMic,
  onSelectCamera,
  onFlipCamera,
  quality,
  onSelectQuality,
  background,
  onSelectBackground,
  onLayerHint,
  myUserID,
  raisedHands,
  reactions,
  onToggleHand,
  onReact,
  annotation,
  annotationAllowed,
  onToggleAnnotation,
  onOpenWhiteboard,
}: {
  readonly active: boolean;
  readonly minimized: boolean;
  readonly oneToOne: boolean; // a transferred 1:1 DM call (present as "Call · peer", not a group)
  readonly channelName: string;
  readonly localStream: MediaStream | null;
  readonly participants: { userID: string; stream: MediaStream | null }[];
  readonly peerQuality?: (userID: string) => Promise<CallQuality>;
  readonly names: Record<string, string>;
  readonly muted: boolean;
  readonly cameraOff: boolean;
  readonly sendingVideo: boolean;
  readonly mics: MediaDeviceInfo[];
  readonly cameras: MediaDeviceInfo[];
  readonly currentMic?: string;
  readonly currentCamera?: string;
  readonly myName: string;
  readonly onToggleMute: () => void;
  readonly onToggleCamera: () => void;
  readonly onShareScreen: () => void;
  readonly sharingScreen: boolean; // THIS device is screen-sharing
  readonly presenting: string[]; // userIDs currently screen-sharing
  readonly mutedUsers: string[]; // userIDs with their mic muted
  readonly onLeave: () => void;
  readonly onMinimize: () => void;
  readonly onRestore: () => void;
  readonly onSelectMic: (deviceId: string) => void;
  readonly onSelectCamera: (deviceId: string) => void;
  readonly onFlipCamera: () => void;
  readonly quality: VideoQuality;
  readonly onSelectQuality: (q: VideoQuality) => void;
  readonly background: BgMode;
  readonly onSelectBackground: (m: BgMode) => void;
  readonly onLayerHint: (publisher: string, layer: string) => void; // SFU simulcast: desired layer per participant
  readonly myUserID: string;
  readonly raisedHands: string[]; // userIDs with a raised hand, in raise order
  readonly reactions: { id: string; userID: string; emoji: string }[]; // transient floating reactions
  readonly onToggleHand: () => void;
  readonly onReact: (emoji: string) => void;
  // WB-010: annotation wiring for a presenting tile's screen share. `roomID` is the call
  // room; `enabledFor(presenterID)` is the gate (my own share is always drawable).
  readonly annotation?: {
    roomID: string;
    myUserID: string;
    color: string;
    enabledFor: (presenterID: string) => boolean;
    sendOp: (op: WireOp) => void;
    register: (handler: (op: WireOp) => void) => () => void;
  } | null;
  readonly annotationAllowed?: boolean; // do I (presenter) allow others to annotate my share
  readonly onToggleAnnotation?: () => void;
  readonly onOpenWhiteboard?: () => void; // open the Mode B shared whiteboard (WB-011)
}) {
  const pip = usePipDrag(onRestore); // hook must precede the early return
  // Manually-pinned participant (click a tile to promote it to the stage); null = follow
  // the active speaker. Cleared automatically if the pinned person leaves the call.
  const [pinned, setPinned] = useState<string | null>(null);
  // Per-peer link quality (polled): a small dot per tile, like the 1:1 call.
  const [qualities, setQualities] = useState<Record<string, CallQuality>>({});
  const participantIds = participants.map((p) => p.userID).join(",");
  useEffect(() => {
    if (!active || !peerQuality) return;
    let alive = true;
    const tick = async () => {
      const ids = participantIds ? participantIds.split(",") : [];
      const next: Record<string, CallQuality> = {};
      await Promise.all(ids.map(async (id) => { next[id] = await peerQuality(id).catch(() => ({ state: "reconnecting" as const })); }));
      if (alive) setQualities(next);
    };
    void tick();
    const t = setInterval(() => void tick(), 4000);
    return () => { alive = false; clearInterval(t); };
  }, [active, peerQuality, participantIds]);
  // Active-speaker detection (hooks must precede the early return): tag the self + each
  // peer by audio energy; "__self" keys our own stream.
  const streamMap: Record<string, MediaStream | null> = { __self: localStream };
  for (const p of participants) streamMap[p.userID] = p.stream;
  const { speaking, dominant } = useSpeaking(streamMap);
  const [spotlight, setSpotlight] = useState(false); // grid vs speaker-spotlight view
  // Participants rail: open by default on a wide screen (desktop/tablet split), closed on
  // mobile (it opens as an overlay drawer there).
  const [rosterOpen, setRosterOpen] = useState(() => matchMedia("(min-width: 900px)").matches);
  const [page, setPage] = useState(0); // grid pagination (large calls)
  const swipeX = useRef<number | null>(null); // touch-swipe start X for paging on mobile
  const [reactOpen, setReactOpen] = useState(false); // emoji-reaction picker open
  const [moreOpen, setMoreOpen] = useState(false); // the "More" overflow menu open
  const [controlsShown, setControlsShown] = useState(true); // auto-hiding chrome (like the 1:1 view)
  // Hide the controls after 10 idle seconds (shown initially so people learn they auto-hide;
  // tap the call to bring them back). Paused while a menu/picker is open.
  useEffect(() => {
    // Touch devices only: on a desktop (mouse) the controls stay put; on a phone they auto-hide.
    if (!active || !controlsShown || moreOpen || reactOpen || !matchMedia("(pointer: coarse)").matches) return;
    const t = setTimeout(() => setControlsShown(false), 10000);
    return () => clearTimeout(t);
  }, [active, controlsShown, moreOpen, reactOpen]);
  useEffect(() => {
    if (!minimized) setControlsShown(true); // show the controls again when returning to the call
  }, [minimized]);
  // Drop a manual pin when that participant leaves the call (fall back to auto-spotlight).
  useEffect(() => {
    if (pinned && pinned !== "__self" && !participants.some((p) => p.userID === pinned)) setPinned(null);
  }, [pinned, participants]);
  // SFU simulcast (P3): ask the server for a per-participant layer matched to how their tile
  // is shown — the staged speaker → high "f"; on-page grid tiles → a size-matched layer;
  // OFF-PAGE (a large paginated call) or minimised → "q" (their video isn't even rendered, so
  // don't burn the SFU's uplink on it). The active speaker on the visible page gets "f" so
  // whoever's talking stays crisp. Diffs against the last sent so we only signal a change.
  const sentLayers = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!active) return;
    const ids = ["__self", ...participants.map((p) => p.userID)];
    const count = ids.length;
    const pinValid = pinned && ids.includes(pinned) ? pinned : null;
    const spotOn = pinValid != null || (spotlight && count >= 3);
    const stage = pinValid ?? ((dominant && ids.includes(dominant) ? dominant : participants[0]?.userID) ?? "__self");
    const per = matchMedia("(min-width: 900px)").matches ? 9 : 4;
    const pageCnt = Math.max(1, Math.ceil(count / per));
    const cur = Math.min(page, pageCnt - 1);
    const onPage = new Set(spotOn ? ids : ids.slice(cur * per, cur * per + per));
    for (const p of participants) {
      let rid: string;
      if (minimized || !onPage.has(p.userID)) rid = "q"; // not visible → lowest
      else if (spotOn) rid = p.userID === stage ? "f" : "q"; // filmstrip thumbs are tiny
      else rid = p.userID === stage ? "f" : gridLayer(Math.min(count, per)); // active speaker crisp
      if (sentLayers.current[p.userID] !== rid) {
        sentLayers.current[p.userID] = rid;
        onLayerHint(p.userID, rid);
      }
    }
  }, [participants, spotlight, pinned, dominant, minimized, active, page, onLayerHint]);
  if (!active) return null;

  const nameOf = (uid: string) => names[uid] || uid.replace(/^@/, "").split(":")[0];
  const count = participants.length + 1;
  if (minimized) {
    // Keep every participant's audio playing while minimised — unmounting the grid
    // would mute the call. The tiles render off-screen (audio only) and a draggable
    // pill returns to the call.
    return (
      <>
        <div className="group-audio-keep" aria-hidden>
          {participants.map((p) => (
            <Tile key={p.userID} stream={p.stream} name={nameOf(p.userID)} quality={qualities[p.userID]} />
          ))}
        </div>
        <button
          className="call-mini"
          ref={pip.ref}
          style={pip.style}
          onPointerDown={pip.onPointerDown}
          onPointerMove={pip.onPointerMove}
          onPointerUp={pip.onPointerUp}
          title="Drag · tap to return to the call"
        >
          <PhoneOutlined /> <span>{oneToOne ? `Call · ${channelName}` : `Group call · ${count}`}</span>
        </button>
      </>
    );
  }

  const deviceItem = (d: MediaDeviceInfo, selected: boolean, label: string, onClick: () => void) => ({
    key: d.deviceId,
    label,
    icon: selected ? <CheckOutlined /> : <span style={{ display: "inline-block", width: 12 }} />,
    onClick,
  });
  const deviceMenu: MenuProps = {
    items: [
      { key: "mic-group", type: "group", label: "Microphone", children: mics.map((d, i) => deviceItem(d, d.deviceId === currentMic, micLabel(d, i), () => onSelectMic(d.deviceId))) },
      ...(cameras.length ? [{ key: "cam-group", type: "group" as const, label: "Camera", children: cameras.map((d, i) => deviceItem(d, d.deviceId === currentCamera, cameraLabel(d, i), () => onSelectCamera(d.deviceId))) }] : []),
      qualityMenuGroup(quality, onSelectQuality),
      ...(cameras.length ? [backgroundMenuGroup(background, onSelectBackground)] : []),
    ],
  };
  // "More" overflow: the device menu PLUS the secondary actions, so the bottom bar stays a
  // handful of buttons on mobile (mute · camera · hand · react · more · leave).
  const showFlip = cameras.length > 1 && sendingVideo && !cameraOff && !sharingScreen;
  const moreMenu: MenuProps = {
    items: [
      ...(deviceMenu.items ?? []),
      { type: "divider" },
      { key: "screen", icon: <FundProjectionScreenOutlined />, label: sharingScreen ? "Stop sharing screen" : "Share screen", onClick: onShareScreen },
      ...(sharingScreen && onToggleAnnotation
        ? [{ key: "annotate", icon: <EditOutlined />, label: annotationAllowed ? "Stop annotations" : "Allow annotations", onClick: onToggleAnnotation }]
        : []),
      ...(showFlip ? [{ key: "flip", icon: <SwapOutlined />, label: "Flip camera", onClick: onFlipCamera }] : []),
      ...(onOpenWhiteboard ? [{ key: "whiteboard", icon: <HighlightOutlined />, label: "Open whiteboard", onClick: onOpenWhiteboard }] : []),
      ...(count >= 3 ? [{ key: "spotlight", icon: spotlight ? <AppstoreOutlined /> : <ExpandOutlined />, label: spotlight ? "Grid view" : "Spotlight speaker", onClick: () => setSpotlight((s) => !s) }] : []),
      { key: "min", icon: <MessageOutlined />, label: "Back to messages", onClick: onMinimize },
    ],
  };

  // Clicking a tile pins that participant to the stage; clicking it again unpins.
  const togglePin = (id: string) => setPinned((cur) => (cur === id ? null : id));
  // Build the annotation overlay config for a presenting participant's tile (WB-010): scoped
  // to that presenter's screen board, gated by their permission (my own share always on).
  const annotationFor = (presenterID: string): AnnotationConfig | null => {
    if (!annotation) return null;
    return {
      author: annotation.myUserID,
      boardId: screenBoardId(annotation.roomID, presenterID),
      color: annotation.color,
      enabled: presenterID === annotation.myUserID ? true : annotation.enabledFor(presenterID),
      sendOp: annotation.sendOp,
      register: annotation.register,
    };
  };
  const tileFor = (id: string) => {
    const common = { pinned: pinned === id, onClick: () => togglePin(id) };
    if (id === "__self") return <Tile key="__self" stream={localStream} name={myName} self presenting={sharingScreen} muted={muted} speaking={speaking.includes("__self")} handRaised={raisedHands.includes(myUserID)} annotation={annotationFor(myUserID)} {...common} />;
    const p = participants.find((x) => x.userID === id);
    if (!p) return null;
    return <Tile key={p.userID} stream={p.stream} name={nameOf(p.userID)} presenting={presenting.includes(p.userID)} muted={mutedUsers.includes(p.userID)} speaking={speaking.includes(p.userID)} handRaised={raisedHands.includes(p.userID)} quality={qualities[p.userID]} annotation={annotationFor(p.userID)} {...common} />;
  };
  const allIds = ["__self", ...participants.map((p) => p.userID)];
  // A pinned tile forces spotlight; otherwise the manual "Spotlight speaker" toggle does
  // (3+ people only). The stage is the pinned tile, else the active speaker, else the first.
  const pinnedValid = pinned && allIds.includes(pinned) ? pinned : null;
  const spotlightOn = pinnedValid != null || (spotlight && count >= 3);
  const stageId = pinnedValid ?? ((dominant && allIds.includes(dominant) ? dominant : participants[0]?.userID) ?? "__self");
  const stripIds = allIds.filter((id) => id !== stageId);
  // Grid pagination (large calls): page the tiles so a big call doesn't cram into unreadable
  // thumbnails. Page size adapts to width; prev/next + dots on desktop, swipe on mobile.
  // Everyone is always reachable in the roster regardless of page.
  const perPage = matchMedia("(min-width: 900px)").matches ? 9 : 4;
  const pageCount = Math.max(1, Math.ceil(allIds.length / perPage));
  const curPage = Math.min(page, pageCount - 1);
  const pageIds = pageCount > 1 ? allIds.slice(curPage * perPage, curPage * perPage + perPage) : allIds;
  const goPage = (d: number) => setPage(Math.max(0, Math.min(pageCount - 1, curPage + d)));

  return (
    <div className={`group-call${controlsShown ? "" : " controls-hidden"}`}>
      <div className="call-head">
        <div className="call-peer">{oneToOne ? channelName : "Group call"}</div>
        <div className="call-status">
          {oneToOne ? (count <= 1 ? "Connecting…" : "In call") : `${channelName} · ${count} ${count === 1 ? "participant" : "participants"}`}
        </div>
      </div>
      {!oneToOne && raisedHands.length > 0 && (
        <div className="call-hands" title="Raised hands, in order">
          ✋ {raisedHands.map((id) => (id === myUserID ? "You" : nameOf(id))).join(", ")}
        </div>
      )}
      {!oneToOne && (
        <div className="call-reactions" aria-hidden>
          {reactions.map((r) => (
            <div key={r.id} className="call-reaction" style={{ left: `${reactionLeft(r.id)}%` }}>
              <span className="call-reaction-emoji">{r.emoji}</span>
              <span className="call-reaction-name">{r.userID === myUserID ? "You" : nameOf(r.userID)}</span>
            </div>
          ))}
        </div>
      )}
      {/* Participants toggle (top-right, like the concept) — opens the roster rail, which
          carries its own close button. */}
      {!oneToOne && !rosterOpen && (
        <Button
          className="group-roster-toggle"
          shape="circle"
          icon={<TeamOutlined />}
          onClick={() => setRosterOpen(true)}
          title="Show participants"
        />
      )}
      <div className="group-body">
        <div className="group-content">
          {spotlightOn ? (
            <div className="group-spotlight" onClick={() => setControlsShown((s) => !s)}>
              {/* Filmstrip on top (horizontally scrollable), the pinned/active speaker below. */}
              <div className="group-strip">{stripIds.map(tileFor)}</div>
              <div className="group-stage">{tileFor(stageId)}</div>
            </div>
          ) : (
            <div
              className="group-grid"
              data-count={Math.min(pageIds.length, 9)}
              onClick={() => setControlsShown((s) => !s)}
              onTouchStart={pageCount > 1 ? (e) => { swipeX.current = e.touches[0]?.clientX ?? null; } : undefined}
              onTouchEnd={
                pageCount > 1
                  ? (e) => {
                      const sx = swipeX.current;
                      swipeX.current = null;
                      if (sx == null) return;
                      const dx = (e.changedTouches[0]?.clientX ?? sx) - sx;
                      if (Math.abs(dx) > 60) goPage(dx < 0 ? 1 : -1); // swipe left → next page
                    }
                  : undefined
              }
            >
              {pageIds.map(tileFor)}
            </div>
          )}
          {!spotlightOn && pageCount > 1 && (
            <div className="group-pager" onClick={(e) => e.stopPropagation()}>
              <Button shape="circle" size="small" icon={<LeftOutlined />} disabled={curPage === 0} onClick={() => goPage(-1)} title="Previous page" />
              <div className="group-pager-dots">
                {Array.from({ length: pageCount }, (_, i) => (
                  <button key={`dot-${i}`} type="button" className={`group-pager-dot${i === curPage ? " active" : ""}`} onClick={() => setPage(i)} title={`Page ${i + 1}`} aria-label={`Page ${i + 1}`} />
                ))}
              </div>
              <Button shape="circle" size="small" icon={<RightOutlined />} disabled={curPage === pageCount - 1} onClick={() => goPage(1)} title="Next page" />
            </div>
          )}
        </div>
        {!oneToOne && rosterOpen && (
          <Roster
            ids={allIds}
            count={count}
            myUserID={myUserID}
            myName={myName}
            nameOf={nameOf}
            muted={muted}
            mutedUsers={mutedUsers}
            sharingScreen={sharingScreen}
            presenting={presenting}
            speaking={speaking}
            raisedHands={raisedHands}
            pinned={pinned}
            onPin={togglePin}
            onClose={() => setRosterOpen(false)}
          />
        )}
      </div>
      <div className="call-controls">
        <Button shape="circle" size="large" icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />} onClick={onToggleMute} title={muted ? "Unmute" : "Mute"} />
        <Button
          shape="circle"
          size="large"
          icon={sendingVideo ? (cameraOff ? <VideoCameraAddOutlined /> : <VideoCameraOutlined />) : <VideoCameraAddOutlined />}
          onClick={onToggleCamera}
          title={sendingVideo ? (cameraOff ? "Camera on" : "Camera off") : "Turn on camera"}
        />
        {/* Reactions + raise-hand are GROUP affordances — meaningless in a 1:1 (a transferred DM
            rides the SFU as a oneToOne call rendered here), so hide them, like the DM nick-list
            suppresses role actions. */}
        {!oneToOne && (
          <Button
            shape="circle"
            size="large"
            type={raisedHands.includes(myUserID) ? "primary" : "default"}
            onClick={onToggleHand}
            title={raisedHands.includes(myUserID) ? "Lower hand" : "Raise hand"}
          >
            ✋
          </Button>
        )}
        {!oneToOne && (
          <Popover
            open={reactOpen}
            onOpenChange={setReactOpen}
            trigger="click"
            placement="top"
            content={
              <EmojiPicker
                onPick={(e) => {
                  onReact(e);
                  setReactOpen(false);
                }}
              />
            }
          >
            <Button shape="circle" size="large" icon={<SmileOutlined />} title="React" />
          </Popover>
        )}
        <Dropdown menu={moreMenu} open={moreOpen} onOpenChange={setMoreOpen} trigger={["click"]} placement="top">
          <Button shape="circle" size="large" icon={<EllipsisOutlined />} title="More (screen share, devices, layout…)" />
        </Dropdown>
        <Button shape="circle" size="large" danger type="primary" icon={<PhoneOutlined rotate={135} />} onClick={onLeave} title="Leave call" />
      </div>
    </div>
  );
}
